import { memo } from "react";
import { motion } from "framer-motion";
import type { NewsItem } from "@/types/news.types";
import { getPhotoUrl } from "@/api/client";
import { Card, Icon, ICONS } from "@/components/kit";

interface NewsCardProps {
  item: NewsItem;
  index: number;
  onClick: (item: NewsItem) => void;
}

function NewsCardComponent({ item, index, onClick }: NewsCardProps) {
  const photoUrl = getPhotoUrl(item.image);

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: Math.min(index, 8) * 0.05 }}
    >
      <Card
        onClick={() => onClick(item)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(item);
          }
        }}
        className="flex cursor-pointer items-center gap-3 p-3 transition-transform active:scale-[0.99]"
      >
        <div className="size-[72px] shrink-0 overflow-hidden rounded-2xl" style={{ background: "var(--c-line)" }}>
          {photoUrl && (
            <img
              src={photoUrl}
              alt={item.title}
              className="size-full object-cover object-center"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium" style={{ color: "var(--c-text-muted)" }}>
            {item.publishDate}
          </p>
          <h5 className="mt-1 line-clamp-2 text-base font-semibold leading-tight" style={{ color: "var(--c-text)" }}>
            {item.title}
          </h5>
        </div>

        <span className="shrink-0" style={{ color: "var(--c-text-muted)" }}>
          <Icon name={ICONS.chevronRight} size={18} />
        </span>
      </Card>
    </motion.div>
  );
}

export const NewsCard = memo(NewsCardComponent);
